import { createSelector } from 'reselect';

import { TQuizAnswer, TQuizAnswerTag, TQuizQuestion } from '@lib/core/quizzes/types';
import QuizUtils from '@lib/core/quizzes/utils';
import { AppStateType } from '@lib/core/service/types/appStateType';

/**
 * @returns the whole quiz state
 */
export const selectQuizState = (state: AppStateType) => state.quiz;

/**
 * @returns loading state for quiz
 */
export const selectQuizFetching = createSelector([selectQuizState], quiz => quiz.isLoading);

/**
 * @returns quiz API errors
 */
export const selectQuizErrorMessage = createSelector([selectQuizState], quiz => quiz.error.message || '');

/**
 * @returns quiz data id is set if the quiz data is pregenerated and needs to be fetched from S3
 */
export const selectQuizDataId = createSelector([selectQuizState], quiz => quiz.data?.quiz_test_data_id || '');

/**
 * @returns an object containing questions with their images
 */
export const selectQuizQuestionImages = createSelector([selectQuizState], quiz => quiz.data?.questions_with_images);

/**
 * @returns the quiz data for views
 */
export const selectQuizData = createSelector([selectQuizState], quiz => quiz.data?.quiz_data || {});

/**
 * @returns the quiz graph
 */
export const selectQuizPath = createSelector([selectQuizState], quiz => quiz.data?.quiz_path || {});

export const selectQuizId = createSelector([selectQuizState], quiz => quiz.data?.identifier || '');

/**
 * @returns the list of tags associated to the quiz answers
 */
export const selectQuizAnswerTags = createSelector([selectQuizData], quizData => {
  const questionIds: string[] = Object.keys(quizData);
  const questions: TQuizQuestion[] = Object.values(quizData);
  const tags = {};

  questions.forEach((question: TQuizQuestion, i) => {
    const { answers } = question;

    answers.forEach((answerData: Record<string, TQuizAnswer>) => {
      const answerTags: TQuizAnswerTag[] = QuizUtils.getTagsFromAnswer(answerData);

      if (answerTags?.length) {
        answerTags.forEach(answerTag => {
          if (!QuizUtils.parseResultQuizAnswerTags(tags).includes(answerTag)) {
            if (!tags[questionIds[i]]) {
              tags[questionIds[i]] = [].concat(answerTags);
            } else {
              tags[questionIds[i]] = tags[questionIds[i]].concat(answerTag);
            }
          }
        });
      }
    });
  });

  return QuizUtils.parseResultQuizAnswerTags(tags) || [];
});

/**
 * @returns min and max choices for each question
 */
export const selectQuizQuestionNumberOfChoices = createSelector(
  [selectQuizState],
  quiz => quiz.data?.number_of_choices || {},
);

/**
 * @returns the question DesignSets for each question
 */
export const selectQuizQuestionDesignSets = createSelector([selectQuizState], quiz => quiz.data?.special_design || {});

/**
 * @returns the ID of the first question in the quiz graph
 */
export const selectQuizFirstQuestionId = createSelector(
  [selectQuizPath],
  quizPath => Object.keys(quizPath).filter(questionId => quizPath[questionId].is_first_question)[0] || '',
);

/**
 * @returns list of all quiz questions
 */
export const selectQuizQuestions = createSelector([selectQuizData], (quizData): TQuizQuestion[] =>
  Object.values(quizData || {}),
);

/**
 * @returns list of all question ids
 */
export const selectQuizQuestionIds = createSelector([selectQuizData], quizData => Object.keys(quizData || {}));
